<script setup lang="ts">
import type { Modules } from '@/types/generated'
import CourseAddEditDialog from '@/components/dialogs/CourseAddEditDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

type CourseData = Modules.Course.CourseData

definePage({
  meta: {
    action: 'manage',
    subject: 'scenarios',
  },
})

const { t } = useI18n()
const router = useRouter()

// Feature flag check (mirrors knowledge-base.vue)
const orgSettings = useCookie<any>('organizationSettings')
if (!(orgSettings.value?.enabledFeatures ?? []).includes('knowledge-base'))
  await router.replace('/games/scenarios/list')

const courses = ref<CourseData[]>([])
const isLoading = ref(true)

const isSnackbarVisible = ref(false)
const snackbarText = ref('')

const isEditDialogVisible = ref(false)
const isConfirmDeleteVisible = ref(false)
const activeCourse = ref<CourseData | null>(null)

const headers = [
  { title: t('courses-page.headers.title'), key: 'title' },
  { title: t('courses-page.headers.description'), key: 'description', sortable: false },
  { title: t('courses-page.headers.documents'), key: 'documents', sortable: false },
  { title: t('courses-page.headers.actions'), key: 'actions', sortable: false },
]

const notify = (text: string) => {
  snackbarText.value = text
  isSnackbarVisible.value = true
}

const fetchCourses = async () => {
  isLoading.value = true
  try {
    courses.value = (await $api<CourseData[]>('/courses')) ?? []
  }
  catch {
    courses.value = []
  }
  finally {
    isLoading.value = false
  }
}

const openCreate = () => {
  activeCourse.value = null
  isEditDialogVisible.value = true
}

const openEdit = (course: CourseData) => {
  activeCourse.value = course
  isEditDialogVisible.value = true
}

const onSaved = async () => {
  notify(activeCourse.value ? t('courses-page.messages.updated') : t('courses-page.messages.created'))
  await fetchCourses()
}

const askDelete = (course: CourseData) => {
  activeCourse.value = course
  isConfirmDeleteVisible.value = true
}

const confirmDelete = async (confirmed: boolean) => {
  isConfirmDeleteVisible.value = false
  if (!confirmed || !activeCourse.value)
    return
  await $api(`/courses/${activeCourse.value.id}`, { method: 'DELETE' })
  notify(t('courses-page.messages.deleted'))
  await fetchCourses()
}

await fetchCourses()
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <h4 class="text-h4">
        {{ t('courses-page.title') }}
      </h4>
      <VBtn
        prepend-icon="tabler-plus"
        @click="openCreate"
      >
        {{ t('courses-page.create') }}
      </VBtn>
    </div>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="courses"
        :loading="isLoading"
        :items-per-page="25"
      >
        <template #item.documents="{ item }">
          {{ item.documentIds?.length ?? 0 }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn @click="openEdit(item)">
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn @click="askDelete(item)">
              <VIcon icon="tabler-trash" />
            </IconBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-disabled py-6">
            {{ t('courses-page.empty') }}
          </div>
        </template>
      </VDataTable>
    </VCard>

    <CourseAddEditDialog
      v-model:is-dialog-visible="isEditDialogVisible"
      :course="activeCourse"
      @submit="onSaved"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteVisible"
      :confirmation-question="t('courses-page.confirm-delete')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmDelete"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      color="success"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
